import { useState } from 'react';

import type { Grupo, Membro, Mes, Roadmap, Tarefa } from '../api/tipos';
import { useMutacoesRoadmap } from './roadmapApi';
import { Avatar } from '../comum/Avatar';
import './kanban.css';

/**
 * Kanban derivado — as mesmas quatro colunas do protótipo. Arrastar um card para
 * "Concluído" marca a frente; tirar de lá desmarca. As demais colunas são consequência
 * da janela de meses, então soltar nelas não muda nada (igual ao original).
 */
const COLUNAS = [
  { id: 'todo', titulo: 'A começar', aceita: (t: Tarefa) => t.status === 'A_COMECAR' },
  { id: 'doing', titulo: 'Em andamento', aceita: (t: Tarefa) => t.status === 'EM_ANDAMENTO' },
  { id: 'late', titulo: 'Em atraso', aceita: (t: Tarefa) => t.status === 'EM_ATRASO' },
  { id: 'done', titulo: 'Concluído', aceita: (t: Tarefa) => t.status === 'CONCLUIDA' },
] as const;

export function Kanban({
  dados,
  filtro,
  abrirFrente,
}: {
  dados: Roadmap;
  filtro: Set<number>;
  abrirFrente(tarefa: Tarefa): void;
}) {
  const { editarTarefa } = useMutacoesRoadmap();
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<string | null>(null);

  const visiveis = dados.tarefas.filter((t) => !filtro.has(t.grupoId));
  const gruposPorId = new Map<number, Grupo>(dados.grupos.map((g) => [g.id, g]));
  const membrosPorId = new Map<number, Membro>(dados.membros.map((m) => [m.id, m]));

  function soltar(coluna: (typeof COLUNAS)[number]['id']) {
    setAlvo(null);
    if (arrastando == null) return;
    const tarefa = dados.tarefas.find((t) => t.id === arrastando);
    setArrastando(null);
    if (!tarefa) return;

    if (coluna === 'done' && !tarefa.concluida) {
      editarTarefa.mutate({ id: tarefa.id, concluida: true });
    } else if (coluna !== 'done' && tarefa.concluida) {
      editarTarefa.mutate({ id: tarefa.id, concluida: false });
    }
  }

  return (
    <div className="kanban">
      {COLUNAS.map((coluna) => {
        const cards = visiveis.filter(coluna.aceita);
        return (
          <div
            key={coluna.id}
            className={`kcol${alvo === coluna.id ? ' over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setAlvo(coluna.id);
            }}
            onDragLeave={() => setAlvo(null)}
            onDrop={() => soltar(coluna.id)}
          >
            <div className="kh">
              {coluna.titulo}
              <span className="kn">{cards.length}</span>
            </div>
            <div className="kbody">
              {cards.length === 0 && <div className="kempty">—</div>}
              {cards.map((tarefa) => {
                const grupo = gruposPorId.get(tarefa.grupoId);
                return (
                  <div
                    key={tarefa.id}
                    className={`kcard${arrastando === tarefa.id ? ' drag' : ''}`}
                    style={{ '--c': grupo?.cor } as React.CSSProperties}
                    draggable
                    onDragStart={() => setArrastando(tarefa.id)}
                    onDragEnd={() => setArrastando(null)}
                    onClick={() => abrirFrente(tarefa)}
                  >
                    {/*
                      A bolinha do que você não viu — a mesma do Dev Board, pelo mesmo
                      motivo e no mesmo canto: os dois quadros são o mesmo gesto de
                      trabalho, e a frente e o card são a mesma tarefa.
                    */}
                    {tarefa.naoVisto && (
                      <span
                        className="kcard-nao-visto"
                        title="Você ainda não abriu esta frente, ou há comentário novo"
                        aria-label="não lido"
                      />
                    )}
                    <div className="kg">{grupo?.nome}</div>
                    <div className="kt">{tarefa.nome}</div>
                    {/* Mesma regra do Dev Board: o nome diz "o quê", a descrição diz
                        "qual", e separa duas frentes de nome parecido sem abrir as duas. */}
                    {tarefa.descricao && <div className="kd">{tarefa.descricao}</div>}
                    {/* Janela, checklist e responsáveis numa linha só — como no rodapé do
                        Dev Board. Eram três blocos empilhados, e numa altura fixa cada um
                        deles é uma linha a menos para a descrição. */}
                    <div className="kpe">
                      <span className="km">
                        {rotuloDaJanela(tarefa, dados.meses)} ·{' '}
                        {tarefa.mesFim - tarefa.mesInicio + 1}m
                      </span>
                      {tarefa.checklist.total > 0 && (
                        <span className="ktag">
                          <span className="ktag-ic">☑</span>
                          {tarefa.checklist.feitos}/{tarefa.checklist.total}
                        </span>
                      )}
                      {tarefa.responsaveis.length > 0 && (
                        <div className="avs">
                          {tarefa.responsaveis.slice(0, 4).map((id) => {
                            const m = membrosPorId.get(id);
                            return m ? (
                              <Avatar
                                key={id}
                                className="av"
                                nome={m.nome}
                                cor={m.cor}
                                url={m.avatar}
                              />
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function rotuloDaJanela(tarefa: Tarefa, meses: Mes[]): string {
  const rotulo = (indice: number) => meses[indice - 1]?.rotulo ?? `M${indice}`;
  return tarefa.mesInicio === tarefa.mesFim
    ? rotulo(tarefa.mesInicio)
    : `${rotulo(tarefa.mesInicio)}–${rotulo(tarefa.mesFim)}`;
}

